import { motion } from 'framer-motion'
import { Layers, Plug, ShieldCheck, Eye, Zap, GitBranch } from 'lucide-react'
import clsx from 'clsx'

const capabilities = [
  {
    Icon: Layers,
    title: 'Cross-product context',
    body: 'Knows your test runs, your tickets, your code, and your docs. One question, queries five places, gives you one answer with citations.',
    accent: 'cyan',
  },
  {
    Icon: Plug,
    title: '12 enterprise integrations',
    body: 'Jira, GitHub, GitLab, Bitbucket, Confluence, Postgres, Jenkins, Slack, SAP, AWS, Azure DevOps, Salesforce. Connect once, ask anything.',
    accent: 'sky',
  },
  {
    Icon: ShieldCheck,
    title: 'Audit + consent on every write',
    body: 'Destructive actions require explicit user approval with a single-shot nonce. Every action lands in an immutable audit log keyed by tenant.',
    accent: 'teal',
  },
  {
    Icon: Eye,
    title: 'Transparent tool calls',
    body: 'Every API call the agent makes is recorded with timing, args, and results. Click any tool card to see exactly what ran.',
    accent: 'cyan',
  },
  {
    Icon: Zap,
    title: 'Function-calling, not regex',
    body: 'Real Gemini function-calling with multi-step planning. The model decides which tools to call and in what order — no hardcoded intent routing.',
    accent: 'sky',
  },
  {
    Icon: GitBranch,
    title: 'BYOK / BYOB',
    body: 'Bring your own LLM API key. Bring your own S3 bucket. Encrypted at rest with per-tenant Fernet keys. Hardened — no silent fallback to platform credentials.',
    accent: 'teal',
  },
]

const accentMap: Record<string, { ring: string; bg: string; text: string }> = {
  cyan: {
    ring: 'group-hover:ring-cyan-500/30',
    bg: 'bg-cyan-500/10',
    text: 'text-cyan-400',
  },
  sky: {
    ring: 'group-hover:ring-sky-500/30',
    bg: 'bg-sky-500/10',
    text: 'text-sky-400',
  },
  teal: {
    ring: 'group-hover:ring-teal-500/30',
    bg: 'bg-teal-500/10',
    text: 'text-teal-400',
  },
}

export function Features() {
  return (
    <section id="capabilities" className="relative bg-zinc-950 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-mono text-xs uppercase tracking-widest text-cyan-400">
            // capabilities
          </p>
          <h2 className="mt-4 text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Built for production test ops
          </h2>
          <p className="mt-4 text-lg text-white/70">
            Reads your tools. Runs your tools. Asks before doing anything destructive —
            with a full audit trail behind every action.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {capabilities.map((cap, i) => {
            const a = accentMap[cap.accent]
            return (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={clsx(
                  'group relative rounded-xl border border-white/10 bg-white/[0.02] p-6 ring-1 ring-transparent transition',
                  'hover:border-white/20 hover:bg-white/[0.04]',
                  'focus-within:border-cyan-400/50 focus-within:ring-cyan-400/30',
                  a.ring,
                )}
              >
                <div
                  className={clsx(
                    'inline-flex h-10 w-10 items-center justify-center rounded-lg',
                    a.bg,
                  )}
                >
                  <cap.Icon className={clsx('h-5 w-5', a.text)} />
                </div>
                <h3 className="mt-5 font-semibold text-white">{cap.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{cap.body}</p>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
